import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { announcementsAPI, getAssetUrl } from '../services/api';
import '../styles/AnnouncementBanner.css';

/**
 * AnnouncementBanner
 *
 * Fetches active announcements for the tenant on mount and shows them as a
 * full-screen modal overlay if the logged-in user hasn't dismissed them yet
 * during this browser session.
 *
 * Dismissal is tracked in sessionStorage so:
 *  - Each user sees each announcement once per login session.
 *  - After 24 h the announcement expires on the server and is never shown again.
 */
const PUBLIC_ROUTES = ['/login', '/register', '/join'];

const AnnouncementBanner = () => {
  const { user, isAuthenticated } = useAuth();
  const location = useLocation();
  const [queue, setQueue] = useState([]);   // unseen active announcements
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!isAuthenticated || !user || PUBLIC_ROUTES.includes(location.pathname)) {
      setQueue([]);
      setVisible(false);
      return;
    }

    const fetchAndFilter = async () => {
      try {
        const res = await announcementsAPI.getActive();
        const all = res.data?.announcements || [];

        // Keep only those the user hasn't dismissed this session
        const unseen = all.filter(
          (a) => !sessionStorage.getItem(`ann_dismissed_${a._id}_${user.id}`)
        );

        if (unseen.length > 0) {
          setQueue(unseen);
          setIndex(0);
          setVisible(true);
        }
      } catch (err) {
        // Silently fail — announcements are non-critical
        console.error('[AnnouncementBanner] fetch error:', err?.message);
      }
    };

    fetchAndFilter();
  }, [isAuthenticated, user, location.pathname]);

  const dismiss = () => {
    // Mark current announcement as seen for this session
    const current = queue[index];
    if (current) {
      sessionStorage.setItem(`ann_dismissed_${current._id}_${user.id}`, '1');
    }

    if (index < queue.length - 1) {
      setIndex((i) => i + 1);
    } else {
      setVisible(false);
    }
  };

  const dismissAll = () => {
    queue.forEach((a) => {
      sessionStorage.setItem(`ann_dismissed_${a._id}_${user.id}`, '1');
    });
    setVisible(false);
  };

  if (!visible || queue.length === 0) return null;

  const announcement = queue[index];
  const showText   = announcement.type === 'text'   || announcement.type === 'both';
  const showPoster = announcement.type === 'poster'  || announcement.type === 'both';
  const total      = queue.length;

  return (
    <div className="announcement-overlay" onClick={(e) => e.target === e.currentTarget && dismissAll()}>
      <div className="announcement-modal" role="dialog" aria-modal="true" aria-labelledby="ann-title">
        <div className="announcement-modal-header">
          <h2 id="ann-title">
            <span className="announcement-badge">Announcement</span>
            {total > 1 && (
              <span className="announcement-counter">{index + 1} / {total}</span>
            )}
          </h2>
          <button
            className="announcement-nav-btn"
            onClick={dismissAll}
            aria-label="Close all announcements"
            title="Close"
          >
            ✕
          </button>
        </div>

        <div className="announcement-modal-body">
          <p className="announcement-title">{announcement.title}</p>

          {showPoster && announcement.posterImage && (
            <img
              className="announcement-poster-img"
              src={getAssetUrl(announcement.posterImage)}
              alt="Announcement poster"
            />
          )}

          {showText && announcement.content && (
            <p className="announcement-content-text">{announcement.content}</p>
          )}

          <p className="announcement-meta">
            Posted by {announcement.createdBy?.username || 'Admin'}&nbsp;&bull;&nbsp;
            Expires {new Date(announcement.expiresAt).toLocaleString()}
          </p>
        </div>

        <div className="announcement-modal-footer">
          {total > 1 && (
            <>
              <div className="announcement-pagination">
                {queue.map((_, i) => (
                  <button
                    key={i}
                    className={`pagination-dot${i === index ? ' active' : ''}`}
                    onClick={() => setIndex(i)}
                    aria-label={`Go to announcement ${i + 1}`}
                  />
                ))}
              </div>
              <button
                className="announcement-nav-btn"
                disabled={index === 0}
                onClick={() => setIndex((i) => i - 1)}
              >
                ← Prev
              </button>
              <button
                className="announcement-nav-btn"
                disabled={index === total - 1}
                onClick={() => setIndex((i) => i + 1)}
              >
                Next →
              </button>
            </>
          )}
          <button className="btn-dismiss" onClick={dismiss}>
            {index < total - 1 ? 'Next →' : 'Got it'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AnnouncementBanner;
